import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ItemGroup } from '@/components/ui/item';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { AttachmentType } from '@/entities/consignments';
import { AttachmentItem } from '@/features/consignment-attachment-list';
import { getAttachmentTypeLabelOptions } from '@/features/consignment-attachment-upload';
import {
  groupAttachmentsBySource,
  toAttachmentSummary,
  useReceiveAttachmentDownload,
  type ReceiveAttachmentDto,
  type ReceivePendingAttachment
} from '@/entities/receive';
import { FileUpload } from '@/shared/ui/file-upload';
import { MAX_FILE_SIZE } from '@/shared/lib';

import { CONFIRM_ARRIVAL_MAX_FILES, CONFIRM_ARRIVAL_TEXT_MAX_LENGTH } from '../model/constants';
import { ConfirmArrivalPendingDocument } from './confirm-arrival-pending-document';

interface ConfirmArrivalDocumentsProps {
  attachments: ReceiveAttachmentDto[];
  allowedTypes: AttachmentType[];
  pending: ReceivePendingAttachment[];
  comment: string;
  onAddFiles: (files: FileList | File[]) => void;
  onTypeChange: (tempId: string, attachmentType: AttachmentType) => void;
  onRemove: (tempId: string) => void;
  onCommentChange: (comment: string) => void;
  /** Heading of the files about to be sent — the release form reuses this block. */
  pendingTitle?: string;
}

interface DocumentGroupProps {
  title: string;
  attachments: ReceiveAttachmentDto[];
  onDownload: (attachment: ReceiveAttachmentDto) => void;
}

/**
 * Stored documents of one origin. The row component is the consignment one, so
 * a file looks the same wherever it is listed; the receive-only identity
 * (`shipmentId`) stays in the closure that downloads it.
 */
function DocumentGroup({ title, attachments, onDownload }: DocumentGroupProps) {
  if (attachments.length === 0) return null;

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      <ItemGroup>
        {attachments.map((attachment) => (
          <AttachmentItem
            key={`${attachment.shipmentId}-${attachment.id}`}
            attachment={toAttachmentSummary(attachment)}
            onDownload={() => onDownload(attachment)}
          />
        ))}
      </ItemGroup>
    </div>
  );
}

/**
 * Documents of the receipt: what the dispatching side sent, what earlier
 * receipts already recorded, and what this one is about to attach.
 *
 * The picker is inline rather than a dialog: the modal is itself a dialog, and
 * nesting one inside it costs a focus trap for no gain — legacy keeps its upload
 * slots inside the same form for the same reason.
 */
export function ConfirmArrivalDocuments({
  attachments,
  allowedTypes,
  pending,
  comment,
  onAddFiles,
  onTypeChange,
  onRemove,
  onCommentChange,
  pendingTitle
}: ConfirmArrivalDocumentsProps) {
  const { t } = useTranslation();
  const { download } = useReceiveAttachmentDownload();

  const groups = useMemo(() => groupAttachmentsBySource(attachments), [attachments]);

  // The backend rejects a type outside its own list (`GO` above all), so the
  // context's list is the source of truth and the shared label options are
  // filtered down to it.
  const typeOptions = useMemo(() => {
    const allowed = new Set<AttachmentType>(allowedTypes);
    return getAttachmentTypeLabelOptions(t).filter((option) => allowed.has(option.value));
  }, [allowedTypes, t]);

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-medium text-foreground">{t('newcmr.attachments.short', 'Attachments')}</h3>

      <DocumentGroup
        title={t('receive.attachments.fromSender', 'Sent with the shipment')}
        attachments={groups.shipment}
        onDownload={download}
      />
      <DocumentGroup
        title={t('receive.attachments.earlierReceipts', 'Recorded by earlier receipts')}
        attachments={groups.receipt}
        onDownload={download}
      />

      {pending.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {pendingTitle ?? t('receive.attachments.toUpload', 'Will be attached to this receipt')}
          </p>
          <ItemGroup>
            {pending.map((attachment) => (
              <ConfirmArrivalPendingDocument
                key={attachment.tempId}
                attachment={attachment}
                typeOptions={typeOptions}
                onTypeChange={onTypeChange}
                onRemove={onRemove}
              />
            ))}
          </ItemGroup>
        </div>
      )}

      <div className="space-y-1.5">
        {/* Legacy's own wording: the label with the limits in brackets. */}
        <p className="text-xs text-muted-foreground">
          {`${t('newcmr.add.attachment.label', 'Upload attachment')} (${t(
            'newcmr.add.attachment.file.type.desc',
            'You can upload up to 5 attachments, with the maximum size of 20Mb each. Only images and pdf files are allowed'
          )})`}
        </p>
        <FileUpload
          accept=".pdf,image/*"
          maxSize={MAX_FILE_SIZE}
          onFilesSelected={onAddFiles}
          allowCapture
          disabled={pending.length >= CONFIRM_ARRIVAL_MAX_FILES}
          className="h-24"
        />
      </div>

      {pending.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="attachmentComment" className="text-sm font-normal">
            {t('receive.attachments.comment', 'Comment for the attached files')}
          </Label>
          <Textarea
            id="attachmentComment"
            rows={2}
            value={comment}
            maxLength={CONFIRM_ARRIVAL_TEXT_MAX_LENGTH}
            onChange={(event) => onCommentChange(event.target.value)}
          />
        </div>
      )}
    </section>
  );
}
